/**
 * ========================================
 * CHATBOT DATA MANAGER MODULE
 * Handles all API calls and data fetching logic
 * ========================================
 */
(function () {
    'use strict';

    // Private state
    let cachedQuestionnaireData = null;
    let cachedRecommendations = null;
    let recommendationsHydratedFromStorage = false;
    let cachedFaceAnalysis = null;

    /**
     * Skin API may return flat fields like SKINTYPE / SPOTS_IMAGE_FAST alongside (or instead of) Tags[].
     * Build Tags from scalar entries only; skip Base64 image blobs so they stay on the object for the FSR UI.
     */
    function normalizeFaceSkinAnalysisPayload(data) {
        if (!data || typeof data !== 'object') return data;
        var existing = data.Tags || data.tags;
        if (Array.isArray(existing) && existing.length > 0) {
            var filtered = existing.filter(function (tag) {
                var tn = (tag.TagName || tag.tagName || '').toUpperCase().replace(/\s+/g, '_');
                if (/_IMAGE/i.test(tn)) return false;
                var tvs = tag.TagValues || tag.tagValues || [];
                if (!Array.isArray(tvs) || !tvs.length) return true;
                var best = tvs.find(function (v) {
                    return v.Value != null && String(v.Value).trim() !== '';
                }) || tvs[0];
                if (!best) return true;
                var u = (best.Units || best.units || '').toString().trim().toLowerCase();
                if (u === 'base64') return false;
                var vv = best.Value != null ? String(best.Value).trim() : '';
                if (vv.length >= 80 && /^[A-Za-z0-9+/=\s]+$/.test(vv)) {
                    if (vv.indexOf('/9j/') === 0 || vv.indexOf('iVBOR') === 0 || vv.indexOf('UklG') === 0) return false;
                }
                return true;
            });
            if (filtered.length === existing.length) return data;
            if (filtered.length === 0) return data;
            var outStrip = {};
            Object.keys(data).forEach(function (k) { outStrip[k] = data[k]; });
            existing.forEach(function (tag) {
                var origName = tag.TagName || tag.tagName;
                if (!origName || typeof origName !== 'string') return;
                var tn = origName.toUpperCase().replace(/\s+/g, '_');
                if (!/_IMAGE/i.test(tn)) return;
                var hasTop = Object.keys(outStrip).some(function (k) {
                    return k.toUpperCase() === tn;
                });
                if (hasTop) return;
                var tvs = tag.TagValues || tag.tagValues || [];
                if (!Array.isArray(tvs) || !tvs.length) return;
                var best = tvs.find(function (v) {
                    return v.Value != null && String(v.Value).trim() !== '';
                }) || tvs[0];
                if (!best) return;
                var val = best.Value != null ? String(best.Value).trim() : (best.value != null ? String(best.value).trim() : '');
                if (!val) return;
                outStrip[origName] = {
                    units: (best.Units || best.units || 'Base64').toString() || 'Base64',
                    value: val,
                    value_name: origName
                };
            });
            outStrip.Tags = filtered;
            outStrip.tags = filtered;
            return outStrip;
        }

        var tagList = [];
        Object.keys(data).forEach(function (key) {
            if (key === 'Tags' || key === 'tags' || key === 'Message' || key === 'message') return;
            var entry = data[key];
            if (!entry || typeof entry !== 'object') return;
            var units = (entry.units || entry.Units || '').toString();
            if (units.toLowerCase() === 'base64' || /_IMAGE/i.test(key)) return;
            var val = entry.value !== undefined && entry.value !== null ? entry.value : entry.Value;
            if (val === undefined || val === null) return;
            var vn = entry.value_name || entry.ValueName || key;
            tagList.push({
                TagName: key,
                tagName: key,
                Status: true,
                status: true,
                TagValues: [{ Value: String(val), Units: units, ValueName: vn }]
            });
        });
        if (tagList.length === 0) return data;
        var out = {};
        Object.keys(data).forEach(function (k) { out[k] = data[k]; });
        out.Tags = tagList;
        out.tags = tagList;
        return out;
    }

    /** Downscale + JPEG re-encode before skin-analysis POST (gallery uploads were full-res → huge JSON, mobile timeouts). */
    function shrinkFaceScanDataUrlForUpload(imageDataUrl, maxDimension, jpegQuality) {
        if (!imageDataUrl || typeof imageDataUrl !== 'string' || imageDataUrl.indexOf('data:') !== 0) {
            return Promise.resolve(imageDataUrl);
        }
        var maxDim = maxDimension || 1920;
        var q = typeof jpegQuality === 'number' ? jpegQuality : 0.9;
        return new Promise(function (resolve) {
            var img = new Image();
            img.onload = function () {
                var w = img.naturalWidth || img.width;
                var h = img.naturalHeight || img.height;
                if (!w || !h) {
                    resolve(imageDataUrl);
                    return;
                }
                var nw = w;
                var nh = h;
                if (w > maxDim || h > maxDim) {
                    if (w > h) {
                        nh = Math.round((h * maxDim) / w);
                        nw = maxDim;
                    } else {
                        nw = Math.round((w * maxDim) / h);
                        nh = maxDim;
                    }
                }
                var canvas = document.createElement('canvas');
                canvas.width = nw;
                canvas.height = nh;
                var ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(imageDataUrl);
                    return;
                }
                ctx.drawImage(img, 0, 0, nw, nh);
                try {
                    resolve(canvas.toDataURL('image/jpeg', q));
                } catch (e) {
                    resolve(imageDataUrl);
                }
            };
            img.onerror = function () {
                resolve(imageDataUrl);
            };
            img.src = imageDataUrl;
        });
    }

    // Dependencies
    // Expects CONFIG to be available via window.ChatbotConfig

    const DataManager = {
        /**
         * Load questionnaire data from API
         * @param {boolean} forceRefresh - whether to bypass cache
         * @returns {Promise<Object>} The questionnaire data
         */
        loadQuestionnaireData: async function (forceRefresh = false) {
            const CONFIG = window.ChatbotConfig;
            try {
                let data = null;
                if (window.ChatbotAPI && typeof window.ChatbotAPI.get === 'function') {
                    data = await window.ChatbotAPI.get(CONFIG.ENDPOINTS.GET_QUESTIONNAIRE, {
                        useCache: !forceRefresh,
                        cacheKey: 'questionnaire_data',
                        timeout: 20000,
                        retries: 2
                    });
                }

                if (!data || !data.questionnaire) {
                    const fetchOptions = forceRefresh ? { cache: 'no-store' } : {};
                    const url = forceRefresh ? `${CONFIG.ENDPOINTS.GET_QUESTIONNAIRE}?_t=${Date.now()}` : CONFIG.ENDPOINTS.GET_QUESTIONNAIRE;
                    const response = await fetch(url, fetchOptions);
                    if (response.ok) {
                        data = await response.json();
                    }
                }

                if (data && data.questionnaire) {
                    cachedQuestionnaireData = data;
                }
                return cachedQuestionnaireData;
            } catch (error) {
                console.error("[Chatbot] Failed to load questionnaire data:", error);
                return null;
            }
        },

        getQuestionnaireData: function () {
            return cachedQuestionnaireData;
        },

        /**
         * Check if the user has already completed the questionnaire
         * @param {Object} userInfo - { id, email, name }
         * @returns {Promise<Object>} { canShowQuestionnaire, recommendations }
         */
        checkQuestionnaireStatus: async function (userInfo) {
            const CONFIG = window.ChatbotConfig;

            // Guest ID logic
            const isGuest = !userInfo.id || userInfo.id === null || userInfo.id === 'null' || userInfo.id === '';
            let userShopifyId;

            if (isGuest) {
                try {
                    let guestId = localStorage.getItem('chatbot_guest_id');
                    if (!guestId || isNaN(guestId.replace('guest_', ''))) {
                        guestId = Date.now().toString() + Math.floor(Math.random() * 1000).toString();
                        localStorage.setItem('chatbot_guest_id', guestId);
                    }
                    userShopifyId = guestId.replace('guest_', '');
                } catch (e) {
                    userShopifyId = Date.now().toString();
                }
            } else {
                userShopifyId = userInfo.id;
            }

            const params = new URLSearchParams();
            params.append('shopify_id', userShopifyId || '');
            params.append('email', userInfo.email || '');
            params.append('name', userInfo.name || '');

            const url = `${CONFIG.ENDPOINTS.DISPLAY_CHECK}?${params.toString()}`;

            try {
                let data;
                if (window.ChatbotAPI && typeof window.ChatbotAPI.get === 'function') {
                    data = await window.ChatbotAPI.get(url, { useCache: false });
                } else {
                    const res = await fetch(url);
                    data = await res.json();
                }

                // If display is false, it means user has history or shouldn't see questionnaire
                if (data === false || (data && data.display === false)) {
                    // Try to fetch existing recommendations
                    let recommendations = null;
                    try {
                        // API SPEC: POST Request, but user_shopify_id is a QUERY param
                        const recUrl = `${CONFIG.ENDPOINTS.GET_EXISTING_RECOMMENDATIONS}?user_shopify_id=${userShopifyId}`;

                        if (window.ChatbotAPI && typeof window.ChatbotAPI.post === 'function') {
                            recommendations = await window.ChatbotAPI.post(recUrl, {});
                        } else {
                            const res = await fetch(recUrl, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({})
                            });
                            recommendations = await res.json();
                        }
                        if (recommendations) {
                            cachedRecommendations = recommendations;
                        }
                    } catch (fetchErr) {
                        console.error("Error fetching recommendations", fetchErr);
                    }

                    return {
                        canShowQuestionnaire: false,
                        recommendations: cachedRecommendations,
                        userShopifyId // Return this as it might be needed
                    };
                }

                return {
                    canShowQuestionnaire: true,
                    recommendations: null,
                    userShopifyId
                };

            } catch (e) {
                console.error("Error checking status", e);
                return { canShowQuestionnaire: true, error: e };
            }
        },

        RECOMMENDATIONS_STORAGE_KEY: 'codeskin_chatbot_recommendations_v1',

        getCachedRecommendations: function () {
            if (cachedRecommendations !== null && cachedRecommendations !== undefined) {
                return cachedRecommendations;
            }
            if (!recommendationsHydratedFromStorage) {
                recommendationsHydratedFromStorage = true;
                try {
                    var stored = localStorage.getItem(this.RECOMMENDATIONS_STORAGE_KEY);
                    if (stored) {
                        cachedRecommendations = JSON.parse(stored);
                        return cachedRecommendations;
                    }
                } catch (e) { /* ignore */ }
            }
            return cachedRecommendations;
        },

        setCachedRecommendations: function (recs) {
            cachedRecommendations = recs;
            recommendationsHydratedFromStorage = true;
            try {
                if (recs != null) {
                    localStorage.setItem(this.RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(recs));
                } else {
                    localStorage.removeItem(this.RECOMMENDATIONS_STORAGE_KEY);
                    if (window.ChatbotMessages && typeof window.ChatbotMessages.clearPersistedGuidedState === 'function') {
                        window.ChatbotMessages.clearPersistedGuidedState();
                    }
                }
            } catch (e) { /* quota / private mode */ }
        },

        /** Face scan analysis from POST /tools/get-skin-analysis-from-image – persisted so it survives reload */
        FACE_ANALYSIS_STORAGE_KEY: 'chatbot_face_analysis',
        getCachedFaceAnalysis: function () {
            if (cachedFaceAnalysis) return cachedFaceAnalysis;
            try {
                var stored = localStorage.getItem(this.FACE_ANALYSIS_STORAGE_KEY);
                if (stored) {
                    var parsed = JSON.parse(stored);
                    cachedFaceAnalysis = normalizeFaceSkinAnalysisPayload(parsed);
                    return cachedFaceAnalysis;
                }
            } catch (e) { /* ignore */ }
            return null;
        },
        setCachedFaceAnalysis: function (data) {
            cachedFaceAnalysis = data != null ? normalizeFaceSkinAnalysisPayload(data) : null;
            try {
                if (cachedFaceAnalysis != null) {
                    localStorage.setItem(this.FACE_ANALYSIS_STORAGE_KEY, JSON.stringify(cachedFaceAnalysis));
                } else {
                    localStorage.removeItem(this.FACE_ANALYSIS_STORAGE_KEY);
                }
            } catch (e) { /* ignore */ }
        },

        /**
         * Upload face scan image
         */
        uploadFaceScan: async function (imageBase64, userInfo) {
            const CONFIG = window.ChatbotConfig;
            if (!imageBase64) return null;

            // Logic to determine user_shopify_id (consistent with checkQuestionnaireStatus)
            const isGuest = !userInfo.id || userInfo.id === null || userInfo.id === 'null' || userInfo.id === '';
            let userShopifyId;

            if (isGuest) {
                try {
                    let guestId = localStorage.getItem('chatbot_guest_id');
                    if (!guestId || isNaN(guestId.replace('guest_', ''))) {
                        guestId = Date.now().toString() + Math.floor(Math.random() * 1000).toString();
                        localStorage.setItem('chatbot_guest_id', guestId);
                    }
                    userShopifyId = guestId.replace('guest_', '');
                } catch (e) {
                    userShopifyId = Date.now().toString();
                }
            } else {
                userShopifyId = userInfo.id.toString();
            }

            var prepared = imageBase64;
            try {
                prepared = await shrinkFaceScanDataUrlForUpload(prepared, 1920, 0.9);
            } catch (shrinkErr) { /* ignore, send original */ prepared = imageBase64; }

            var rawBase64 = prepared.includes(',') ? prepared.split(',')[1] : prepared;
            rawBase64 = String(rawBase64).replace(/^\ufeff/, '').replace(/\s/g, '');

            var uidStr = String(userShopifyId);
            /* OpenAPI: POST body only — { user_shopify_id, image_base64 }; no query parameters. */
            var skinAnalysisUrl =
                CONFIG.ENDPOINTS.GET_SKIN_ANALYSIS ||
                'https://skin-analysis-api.fly.dev/api/v1/analyze-skin';

            const payload = {
                user_shopify_id: uidStr,
                image_base64: rawBase64
            };

            var faceScanTimeoutMs = 90000;

            let responseData;
            if (window.ChatbotAPI && typeof window.ChatbotAPI.post === 'function') {
                responseData = await window.ChatbotAPI.post(skinAnalysisUrl, payload, {
                    timeout: faceScanTimeoutMs,
                    retries: 0
                });
            } else {
                const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
                const timeoutId = controller ? setTimeout(function () { controller.abort(); }, faceScanTimeoutMs) : null;
                var res = await fetch(skinAnalysisUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    signal: controller ? controller.signal : undefined
                });
                if (timeoutId) clearTimeout(timeoutId);
                if (res.ok) {
                    responseData = await res.json();
                }
            }

            return responseData;
        },

        /**
         * Clear chatbot caches that can leak stale state across retake flows.
         * @param {boolean} preserveFaceAnalysis - if true, don't clear the face scan analysis (useful for questionnaire-only retakes)
         */
        clearAllCachedState: function (preserveFaceAnalysis = false) {
            cachedQuestionnaireData = null;
            cachedRecommendations = null;
            recommendationsHydratedFromStorage = false;

            try {
                localStorage.removeItem(this.RECOMMENDATIONS_STORAGE_KEY);
                localStorage.removeItem('codeskin_chatbot_last_guided_product_id');
                localStorage.removeItem('codeskin_chatbot_guided_state_v1');
                localStorage.removeItem('chatbot_last_routine_total');
                localStorage.removeItem('chatbot_last_routine_in_count');
                localStorage.removeItem('chatbot_last_cart_item_count');
                
                if (!preserveFaceAnalysis) {
                    cachedFaceAnalysis = null;
                    localStorage.removeItem(this.FACE_ANALYSIS_STORAGE_KEY);
                }
            } catch (e) { /* ignore */ }

            if (window.ChatbotMessages && typeof window.ChatbotMessages.clearPersistedGuidedState === 'function') {
                window.ChatbotMessages.clearPersistedGuidedState();
            }
            if (window.ChatbotAPI && typeof window.ChatbotAPI.clearCache === 'function') {
                // Clears in-memory request/data cache in chatbot-api-utils.js
                window.ChatbotAPI.clearCache();
            }
        }

    };

    // Expose
    window.ChatbotDataManager = DataManager;

})();
